/**
 * Zustands-Kontrakt und Seiten-Registry des ListPage-Gerüsts (D-W1 Welle 2,
 * A2-13). Reine Daten und Logik ohne React, damit der Kontrakt-Test
 * (listPage.contract.test.ts) in Node läuft — dasselbe Muster wie
 * navModel.ts/navModel.test.ts.
 *
 * Der Kontrakt: jede klassische Listen-Seite bedient VIER Zustände —
 * Laden (PageSkeleton), Fehler (einheitliche Fehlerfläche mit „Erneut
 * laden"; der globale A2-6-Fehler-Toast bleibt und wird nur ergänzt),
 * Leer (EmptyState) und Gefüllt. Erzwungen wird er zweifach:
 * 1. per Typ — `ListPage` verlangt `state`, `errorText`, `empty` und
 *    `children` als Pflicht-Props (tsc ist Teil des CI-Gates);
 * 2. per Registry-Scan — der Kontrakt-Test iteriert über LIST_PAGE_REGISTRY
 *    und schlägt fehl, wenn eine migrierte Seite das Gerüst (und damit einen
 *    der vier Zustände) nicht mehr bedient.
 */
export interface ListPageQueryState {
  /** Erstes Laden → PageSkeleton (Brief §7: Skeleton statt Spinner). */
  isLoading: boolean;
  /** Query-Fehler → Fehlerfläche mit Retry (ergänzt den A2-6-Toast). */
  isError: boolean;
  /** „Erneut laden" der Fehlerfläche, i. d. R. `() => void query.refetch()`. */
  retry: () => void;
  /** Ergebnismenge leer → Leerzustand der Seite (EmptyState). */
  isEmpty: boolean;
}

export type ListStateName = 'loading' | 'error' | 'empty' | 'filled';

/**
 * Reine Zustandsweiche: Laden vor Fehler vor Leer vor Gefüllt. „Laden zuerst"
 * deckt auch den Retry nach einem Fehler ab (TanStack lässt isError bis zum
 * Erfolg stehen); „Fehler vor Leer" verhindert, dass ein Fehlschlag als
 * harmlose leere Liste erscheint.
 */
export function resolveListState(state: ListPageQueryState): ListStateName {
  if (state.isLoading) return 'loading';
  if (state.isError) return 'error';
  if (state.isEmpty) return 'empty';
  return 'filled';
}

/**
 * Vollanalyse S6: Kopf-Meta der Listen-Seiten (D-W1 QS-3) — Beschreibung und
 * aktive Filter, „lädt…" solange keine Daten da sind. Vorher stand derselbe
 * metaParts/filter(Boolean)/join(' · ')-Block wortgleich in vier Seiten
 * (Kunden, Belegjournal, Artikel, Aufträge); neue ListPage-Seiten übernehmen
 * das Muster jetzt von hier.
 */
export function listMeta(
  loaded: boolean,
  fallback: string,
  ...parts: ReadonlyArray<string | null | undefined | false>
): string {
  if (!loaded) return 'lädt…';
  const active = parts.filter(Boolean);
  return active.length > 0 ? active.join(' · ') : fallback;
}

/**
 * F1 (Optimierung 2026-08-25): das state-Objektliteral stand 13x wortgleich
 * in den Seiten — jetzt EIN Erzeuger. `isEmpty` bleibt Aufrufer-Sache (die
 * Leerformeln unterscheiden sich fachlich: Liste, items, entries).
 */
export function listState(
  q: { isLoading: boolean; isError: boolean; refetch: () => unknown },
  isEmpty: boolean,
): ListPageQueryState {
  return {
    isLoading: q.isLoading,
    isError: q.isError,
    retry: () => void q.refetch(),
    isEmpty,
  };
}

/**
 * F3 (Optimierung 2026-08-25): die „Erste 200 Treffer"-Fusszeile stand
 * byte-gleich in Kunden- und Artikelliste; die Kappungsgrenze der API-Listen
 * lebt damit an EINER Stelle.
 */
export function listFooter(
  daten: readonly unknown[] | undefined,
  singular: string,
  plural: string,
  formatCount: (n: number, s: string, p: string) => string,
  cap = 200,
): string | undefined {
  if (!daten) return undefined;
  return daten.length === cap
    ? `Erste ${cap} Treffer — Suche verfeinern`
    : formatCount(daten.length, singular, plural);
}

export interface ListPageRegistryEntry {
  /** Route der Seite (Abgleich mit router.tsx, Dokumentation). */
  route: string;
  /** Quelldatei relativ zu apps/web — der Kontrakt-Test liest den Quelltext. */
  file: string;
}

/**
 * Alle auf das Gerüst migrierten Listen-Seiten. Bewusst NICHT enthalten:
 * QuotesPage (Angebots-Arbeitsvorrat ist eine Pipeline-Ansicht mit
 * SegmentedControl-Werkliste und eigener Entwürfe-Karte, keine klassische
 * Liste), AttendancePage (Monatskonto), AnalyticsPage (Berichtskarten),
 * Plantafel/Beleg-Editor (Umbau gesperrt) sowie Detail- und
 * Einstellungs-Seiten. Neue Listen-Seiten gehören hier hinein — der
 * Kontrakt-Test erzwingt umgekehrt, dass jede ListPage-Nutzung registriert
 * ist.
 */
export const LIST_PAGE_REGISTRY: readonly ListPageRegistryEntry[] = [
  { route: '/documents', file: 'src/pages/DocumentsPage.tsx' },
  { route: '/orders', file: 'src/pages/OrdersPage.tsx' },
  { route: '/customers', file: 'src/pages/CustomersPage.tsx' },
  { route: '/articles', file: 'src/pages/ArticlesPage.tsx' },
  { route: '/price-lists', file: 'src/pages/PriceListsPage.tsx' },
  { route: '/users', file: 'src/pages/UsersPage.tsx' },
  // Frei editierbare Rollen (2026-08-20): eigene Seite unter Benutzer.
  { route: '/roles', file: 'src/pages/RolesPage.tsx' },
  // P2-M12: gerätneutrale Geräteverwaltung nutzt dasselbe Listen-Gerüst.
  { route: '/devices', file: 'src/pages/DevicesPage.tsx' },
  { route: '/recurring', file: 'src/pages/RecurringPage.tsx' },
  { route: '/open-items', file: 'src/pages/OpenItemsPage.tsx' },
  { route: '/dunning', file: 'src/pages/DunningPage.tsx' },
  { route: '/activity', file: 'src/pages/ActivityPage.tsx' },
];
